import axios from 'axios'
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router/composables'

import { eventModule } from '@/store'

// Requests go through the Nuxt proxy (/mcpApi → MCP_BASE_URL) to avoid CORS issues.
const MCP_API_PREFIX = '/mcpApi'

export interface McpOAuthParams {
  clientId: string
  redirectUri: string
  state: string
  codeChallenge: string
}

/**
 * Detects MCP OAuth query params and provides a function to exchange
 * Lambus tokens for an MCP authorization code redirect URL.
 */
export function useMcpAuth() {
  const route = useRoute()
  const router = useRouter()

  /**
   * Returns the MCP OAuth params from the current route query,
   * or null if not in an MCP OAuth flow.
   */
  const mcpOAuthParams = computed<McpOAuthParams | null>(() => {
    const clientId = route.query.client_id
    const redirectUri = route.query.redirect_uri

    if (typeof clientId !== 'string' || typeof redirectUri !== 'string') {
      return null
    }

    return {
      clientId,
      redirectUri,
      state: typeof route.query.state === 'string' ? route.query.state : '',
      codeChallenge:
        typeof route.query.code_challenge === 'string'
          ? route.query.code_challenge
          : '',
    }
  })

  const isMcpOAuthFlow = computed(() => mcpOAuthParams.value !== null)

  /**
   * Exchanges the given Lambus tokens for an MCP authorization code
   * and returns the redirect URL to send the user to.
   *
   * @throws If the token exchange fails.
   */
  async function exchangeTokensForAuthCode(
    lambusToken: string,
    lambusRefreshToken: string,
  ) {
    const params = mcpOAuthParams.value
    if (!params) {
      throw new Error('Not in an MCP OAuth flow')
    }

    const { data } = await axios.post<{ redirectUrl: string }>(
      `${MCP_API_PREFIX}/auth/token-exchange`,
      {
        token: lambusToken,
        refreshToken: lambusRefreshToken,
        client_id: params.clientId,
        redirect_uri: params.redirectUri,
        state: params.state,
        code_challenge: params.codeChallenge,
      },
    )

    return data.redirectUrl
  }

  /**
   * Completes the MCP OAuth flow: exchanges tokens, stores the redirect URL
   * in sessionStorage, and navigates to the auth-complete page.
   *
   * @returns true if the MCP flow was handled, false otherwise.
   */
  async function completeMcpAuth(authResponse: {
    token: string
    refreshToken: string
  }) {
    if (!isMcpOAuthFlow.value) {
      return false
    }

    try {
      const redirectUrl = await exchangeTokensForAuthCode(
        authResponse.token,
        authResponse.refreshToken,
      )
      sessionStorage.setItem('mcp_redirect_url', redirectUrl)
      await router.push({ path: '/mcp/auth-complete' })
    } catch {
      eventModule.newError('error.generic')
    }

    return true
  }

  return {
    isMcpOAuthFlow,
    mcpOAuthParams,
    completeMcpAuth,
  }
}
